import { createReducer, createSelector, on } from '@ngrx/store';
import { startOfDay, subDays } from 'date-fns';
import endOfDay from 'date-fns/endOfDay';
import { OrganisationContextSwitch } from '../actions/meta.actions';
import * as actions from './statistics.actions';
import {
  AverageDaysBetweenPostsDto,
  CommentDto,
  CustomerActivityDto,
  EnquiriesOrgStatsDto,
  EventsPerDayDto,
  MemberActivityDto,
  OrganisationActivityDto,
  OrganisationPositionedListItem,
  WrappedStatisticsResponseDto,
} from '@careapp-group/ng-sdk';
import { Memos } from '../actions';

export interface State {
  customers: {
    loading: boolean;
    error?: any;
    data?: Array<CustomerActivityDto>;
  };
  error?: string;
  filters?: {
    startDate: Date;
    endDate: Date;
  };
  members: {
    loading: boolean;
    error?: any;
    data?: MemberActivityDto[];
  };
  family: {
    loading: boolean;
    error?: any;
    current?: MemberActivityDto[];
    previous?: MemberActivityDto[];
  };
  memosByCustomer?: {
    loading: boolean;
    data: { [id: string]: Object };
  };

  recentMemoComments?: {
    loading: boolean;
    error?: any;
    data?: CommentDto[];
  };

  recentMemoCommentsSummary?: {
    loading: boolean;
    error?: any;
    summary?: string;
  };

  organisationActivityCurrent: { [id: string]: OrganisationActivityDto };
  organisationActivityPrevious: { [id: string]: OrganisationActivityDto };

  enquiriesCurrent: { [id: string]: EnquiriesOrgStatsDto };
  enquiriesPrevious: { [id: string]: EnquiriesOrgStatsDto };
  enquiriesLoading: boolean;

  currentMostActiveOrganisations: OrganisationPositionedListItem[];
  previousMostActiveOrganisations: OrganisationPositionedListItem[];
  mostActiveOrganisationsLoading: boolean;

  currentMostConnectedOrganisations: OrganisationPositionedListItem[];
  previousMostConnectedOrganisations: OrganisationPositionedListItem[];
  mostConnectedOrganisationsLoading: boolean;

  organisationContentCountCurrent: EventsPerDayDto[];
  organisationContentCountPrevious: EventsPerDayDto[];

  organisationDaysBetweenContentCurrent: AverageDaysBetweenPostsDto[];
  organisationDaysBetweenContentPrevious: AverageDaysBetweenPostsDto[];

  engagement?: EventsPerDayDto[];
  organisationActivityLoading: boolean;
  engagementLoading: boolean;
  organisationDaysBetweenContentLoading: boolean;

  topMomentsLoading: boolean;
  organisationContentCountLoading: boolean;

  wrapped: {
    loading: boolean;
    statistics: { [id: string]: WrappedStatisticsResponseDto };
    error?: any;
  };
}

export const initialState: State = {
  filters: {
    startDate: subDays(startOfDay(new Date()), 30),
    endDate: endOfDay(new Date()),
  },
  customers: {
    loading: false,
  },

  family: {
    loading: false,
  },
  members: {
    loading: false,
  },
  memosByCustomer: {
    loading: false,
    data: {},
  },

  wrapped: {
    loading: false,
    statistics: {},
  },

  recentMemoComments: {
    error: null,
    loading: false,
    data: [],
  },
  recentMemoCommentsSummary: {
    error: null,
    loading: false,
    summary: '',
  },

  enquiriesCurrent: {},
  enquiriesPrevious: {},
  enquiriesLoading: false,

  organisationContentCountCurrent: [],
  organisationContentCountPrevious: [],
  organisationActivityCurrent: {},
  organisationActivityPrevious: {},

  currentMostActiveOrganisations: [],
  previousMostActiveOrganisations: [],

  currentMostConnectedOrganisations: [],
  previousMostConnectedOrganisations: [],

  organisationActivityLoading: false,
  engagementLoading: false,
  topMomentsLoading: false,
  organisationContentCountLoading: false,
  organisationDaysBetweenContentLoading: false,
  mostActiveOrganisationsLoading: false,
  mostConnectedOrganisationsLoading: false,

  organisationDaysBetweenContentCurrent: [],
  organisationDaysBetweenContentPrevious: [],
};

export const statsReducer = createReducer(
  initialState,
  on(OrganisationContextSwitch, (state) => initialState),
  on(actions.SetDateRangeAction, (state, { earliest, latest }) => ({
    ...state,
    filters: {
      ...state.filters,
      startDate: earliest,
      endDate: latest,
    },
  })),
  on(actions.RequestMembersStatisticsAction, (state) => ({
    ...state,
    members: { ...state.members, loading: true },
  })),
  on(actions.ResponseSuccessMembersStatisticsAction, (state, { payload }) => ({
    ...state,
    members: { loading: false, data: payload },
  })),
  on(actions.ResponseFailMembersStatisticsAction, (state, { error }) => ({
    ...state,
    members: {
      ...state.members,
      loading: false,
      error,
    },
  })),
  on(actions.RequestCustomersStatisticsAction, (state) => ({
    ...state,
    customers: { ...state.customers, loading: true },
  })),
  on(actions.ResponseSuccessCustomersStatisticsAction, (state, { payload }) => ({
    ...state,
    customers: { loading: false, data: payload },
  })),
  on(actions.ResponseFailCustomersStatisticsAction, (state, { error }) => ({
    ...state,
    customers: {
      ...state.customers,
      loading: false,
      error,
    },
  })),
  on(actions.RequestOrganisationActivityAction, (state) => ({
    ...state,
    organisationActivityLoading: true,
  })),
  on(actions.ResponseSuccessOrganisationActivityAction, (state, { current, previous }) => ({
    ...state,
    organisationActivityCurrent: { ...state.organisationActivityCurrent, ...current },
    organisationActivityPrevious: previous
      ? { ...state.organisationActivityPrevious, ...previous }
      : state.organisationActivityPrevious,
    organisationActivityLoading: false,
  })),
  on(actions.ResponseFailOrganisationActivityAction, (state, { error }) => ({
    ...state,
    organisationActivityLoading: false,
    error,
  })),
  on(actions.RequestOrganisationEngagement, (state) => ({
    ...state,
    engagementLoading: true,
  })),
  on(actions.ResponseSuccessEngagement, (state, { data }) => ({
    ...state,
    engagement: data,
    engagementLoading: false,
  })),
  on(actions.ResponseFailEngagement, (state, { error }) => ({
    ...state,
    engagementLoading: false,
    error,
  })),
  on(actions.RequestOrganisationContentCountPerDay, (state) => ({
    ...state,
    organisationContentCountLoading: true,
  })),
  on(
    actions.ResponseSuccessOrganisationContentCountPerDayAction,
    (state, { current, previous }) => ({
      ...state,
      organisationContentCountPrevious: previous,
      organisationContentCountCurrent: current,
      organisationContentCountLoading: false,
    }),
  ),
  on(actions.ResponseFailOrganisationContentCountPerDayAction, (state, { error }) => ({
    ...state,
    organisationContentCountLoading: false,
    error,
  })),
  on(actions.RequestOrganisationDaysBetweenContent, (state) => ({
    ...state,
    organisationDaysBetweenContentLoading: true,
  })),
  on(
    actions.ResponseSuccessOrganisationDaysBetweenContentAction,
    (state, { current, previous }) => ({
      ...state,
      organisationDaysBetweenContentPrevious: previous,
      organisationDaysBetweenContentCurrent: current,
      organisationDaysBetweenContentLoading: false,
    }),
  ),
  on(actions.ResponseFailOrganisationDaysBetweenContentAction, (state, { error }) => ({
    ...state,
    organisationDaysBetweenContentLoading: false,
    error,
  })),
  on(actions.RequestMostActiveOrganisationsForTenant, (state) => ({
    ...state,
    mostActiveOrganisationsLoading: true,
  })),
  on(actions.ResponseSuccessMostActiveOrganisationsForTenant, (state, { current, previous }) => ({
    ...state,
    previousMostActiveOrganisations: previous,
    currentMostActiveOrganisations: current,
    mostActiveOrganisationsLoading: false,
  })),
  on(actions.ResponseFailMostActiveOrganisationsForTenant, (state, { error }) => ({
    ...state,
    mostActiveOrganisationsLoading: false,
    error,
  })),
  on(actions.RequestMostConnectedOrganisationsForTenant, (state) => ({
    ...state,
    mostConnectedOrganisationsLoading: true,
  })),
  on(
    actions.ResponseSuccessMostConnectedOrganisationsForTenant,
    (state, { current, previous }) => ({
      ...state,
      previousMostConnectedOrganisations: previous,
      currentMostConnectedOrganisations: current,
      mostConnectedOrganisationsLoading: false,
    }),
  ),
  on(actions.ResponseFailMostConnectedOrganisationsForTenant, (state, { error }) => ({
    ...state,
    mostConnectedOrganisationsLoading: false,
    error,
  })),
  on(actions.RequestFamilyLeaderboardAction, (state) => ({
    ...state,
    family: { ...state.family, loading: true },
  })),
  on(actions.ResponseSuccessFamilyLeaderboardAction, (state, { current, previous }) => ({
    ...state,
    family: { loading: false, current, previous },
  })),
  on(actions.ResponseFailFamilyLeaderboardAction, (state, { error }) => ({
    ...state,
    family: {
      ...state.family,
      loading: false,
      error,
    },
  })),
  on(actions.RequestTopMoments, (state) => ({
    ...state,
    topMomentsLoading: true,
  })),
  on(Memos.responseSuccessAllPaginatedMemos, (state, { paginator }) => {
    if (paginator === 'tenant-insights' || paginator === 'organisation-insights') {
      return {
        ...state,
        topMomentsLoading: false,
      };
    }
    return state;
  }),
  on(actions.ResponseFailTopMoments, (state, { error }) => ({
    ...state,
    topMomentsLoading: false,
    error,
  })),
  // Wrapped
  on(actions.RequestWrapped, (state) => ({
    ...state,
    wrapped: { ...state.wrapped, loading: true },
  })),
  on(actions.ResponseSuccessWrapped, (state, { data }) => ({
    ...state,
    wrapped: {
      ...state.wrapped,
      loading: true,
      statistics: { ...state.wrapped.statistics, [`${data.modelName}:${data.modelId}`]: data },
    },
  })),
  on(actions.ResponseFailWrapped, (state, { error }) => ({
    ...state,
    wrapped: {
      ...state.wrapped,
      loading: true,
      error,
    },
  })),
  on(actions.RequestRecentMemoComments, (state) => ({
    ...state,
    recentMemoComments: { ...state.recentMemoComments, loading: true },
    recentMemoCommentsSummary: { ...state.recentMemoCommentsSummary, loading: true },
  })),
  on(actions.ResponseSuccessRecentMemoComments, (state, { data }) => ({
    ...state,
    recentMemoComments: { ...state.recentMemoComments, loading: false, data },
  })),
  on(actions.ResponseFailRecentMemoComments, (state, { error }) => ({
    ...state,
    recentMemoComments: { ...state.recentMemoComments, loading: false, error },
  })),
  on(actions.ResponseSuccessRecentMemoCommentsSummary, (state, { data }) => ({
    ...state,
    recentMemoCommentsSummary: {
      ...state.recentMemoCommentsSummary,
      loading: false,
      summary: data,
    },
  })),
  on(actions.ResponseFailRecentMemoCommentsSummary, (state, { error }) => ({
    ...state,
    recentMemoCommentsSummary: { ...state.recentMemoCommentsSummary, loading: false, error },
  })),
  on(actions.RequestEnquiriesStatsAction, (state) => ({
    ...state,
    enquiriesLoading: true,
  })),
  on(actions.ResponseSuccessEnquiriesStatsAction, (state, { current, previous }) => ({
    ...state,
    enquiriesCurrent: { ...state.enquiriesCurrent, ...current },
    enquiriesPrevious: previous
      ? { ...state.enquiriesPrevious, ...previous }
      : state.enquiriesPrevious,
    enquiriesLoading: false,
  })),
  on(actions.ResponseFailEngagement, (state, { error }) => ({
    ...state,
    enquiriesLoading: false,
    error,
  })),
);

export const createSelectors = (rootReducer) => ({
  getOrganisationsDaysBetweenContent: createSelector(
    rootReducer,
    (state: State) => state.organisationDaysBetweenContentCurrent,
  ),
  getOrganisationsPreviousDaysBetweenContent: createSelector(
    rootReducer,
    (state: State) => state.organisationDaysBetweenContentPrevious,
  ),
  getWrappedEntities: createSelector(rootReducer, (state: State) => state.wrapped.statistics),
  getOrganisationsCurrentActivity: createSelector(
    rootReducer,
    (state: State) => state.organisationActivityCurrent,
  ),
  getOrganisationsPreviousActivity: createSelector(
    rootReducer,
    (state: State) => state.organisationActivityPrevious,
  ),
  getPreviousMostActiveOrganisations: createSelector(
    rootReducer,
    (state: State) => state.previousMostActiveOrganisations,
  ),
  getCurrentMostActiveOrganisations: createSelector(
    rootReducer,
    (state: State) => state.currentMostActiveOrganisations,
  ),
  getPreviousMostConnectedOrganisations: createSelector(
    rootReducer,
    (state: State) => state.previousMostConnectedOrganisations,
  ),
  getCurrentMostConnectedOrganisations: createSelector(
    rootReducer,
    (state: State) => state.currentMostConnectedOrganisations,
  ),
  getOrganisationContentCountCurrent: createSelector(
    rootReducer,
    (state: State) => state.organisationContentCountCurrent,
  ),
  getOrganisationsContentCountPrevious: createSelector(
    rootReducer,
    (state: State) => state.organisationContentCountPrevious,
  ),
  getRecentMemoComments: createSelector(
    rootReducer,
    (state: State) => state.recentMemoComments.data,
  ),
  getRecentMemoCommentsSummary: createSelector(
    rootReducer,
    (state: State) => state.recentMemoCommentsSummary.summary,
  ),
  getRecentMemoCommentsLoading: createSelector(
    rootReducer,
    (state: State) => state.recentMemoComments.loading,
  ),
  getRecentMemoCommentsSummaryLoading: createSelector(
    rootReducer,
    (state: State) => state.recentMemoCommentsSummary.loading,
  ),
  getFamilyCurrentActivity: createSelector(rootReducer, (state: State) => state.family.current),
  getFamilyPreviousActivity: createSelector(rootReducer, (state: State) => state.family.previous),
  organisationActivityLoading: createSelector(
    rootReducer,
    (state: State) => state.organisationActivityLoading,
  ),
  getCustomersStats: createSelector(rootReducer, (state: State) => state.customers),
  getFilters: createSelector(rootReducer, (state: State) => state.filters),
  getMembersStats: createSelector(rootReducer, (state: State) => state.members),

  getEnquiriesCurrent: createSelector(rootReducer, (state: State) => state.enquiriesCurrent),
  getEnquiriesPrevious: createSelector(rootReducer, (state: State) => state.enquiriesPrevious),
  enquiriesLoading: createSelector(rootReducer, (state: State) => state.enquiriesLoading),
});
